// Helper function to get the base URL for API calls
const getBaseUrl = () => {
    if (window.location.hostname === 'localhost') {
        return 'http://localhost:8002';
    }
    return 'https://training-service-fqasamg9euatcaa8.westus2-01.azurewebsites.net';
};

/**
 * Extracts a thumbnail from a video blob at a specified time
 * @param {Blob} videoBlob - The video blob to extract thumbnail from
 * @param {number} [timeInSeconds=1] - Time in seconds to extract thumbnail from
 * @returns {Promise<Blob>} A promise that resolves with the thumbnail blob
 */
export const extractThumbnail = async (videoBlob, timeInSeconds = 1) => {
    return new Promise((resolve, reject) => {
        // Create a video element
        const video = document.createElement('video');
        video.src = URL.createObjectURL(videoBlob);
        video.currentTime = timeInSeconds;
        video.muted = true;
        video.playsInline = true;

        // Handle video load
        video.addEventListener('loadeddata', () => {
            try {
                // Create a canvas to draw the frame
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                // Convert canvas to Blob (PNG format)
                canvas.toBlob(blob => {
                    if (blob) {
                        // Clean up
                        URL.revokeObjectURL(video.src);
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to create thumbnail: Canvas is empty'));
                    }
                }, 'image/png');
            } catch (err) {
                reject(new Error(`Failed to create thumbnail: ${err.message}`));
            }
        });

        // Handle errors
        video.addEventListener('error', (err) => {
            URL.revokeObjectURL(video.src);
            reject(new Error(`Failed to load video: ${err.message}`));
        });
    });
};

/**
 * Fetches training videos for the currently authenticated user with pagination support.
 * Videos are retrieved from the training service and require a valid JWT token.
 * If viewAllUsers is true and user is admin, fetches videos from all users.
 * 
 * @param {Function} setVideos - React state setter to update the videos array
 * @param {Function} setError - React state setter to update the error message
 * @param {boolean} viewAllUsers - Whether to fetch videos from all users (requires admin role)
 * @param {number} offset - Number of videos to skip
 * @param {number} limit - Maximum number of videos to fetch
 * @param {boolean} append - Whether to append the fetched videos to existing ones
 * @param {Array} filters - Array of filters to apply to the videos
 * @param {string} batchId - Optional batch ID to filter videos
 * @returns {Promise<Object>} Object containing success status and pagination metadata
 */
export const fetchVideos = async (
    setVideos,
    setError,
    viewAllUsers = false,
    offset = 0,
    limit = 20,
    append = false,
    filters = [],
    batchId = null,
    userId = null,
    page = 'training'
) => {
    try {
        const token = localStorage.getItem('jwt');
        if (!token) {
            throw new Error('No authentication token found');
        }

        const queryParams = new URLSearchParams({
            offset: offset.toString(),
            limit: limit.toString(),
            all_users: viewAllUsers.toString()
        });

        if (userId) {
            queryParams.append('userId', userId);
        }

        // Add filters to query params if they exist
        if (filters.length > 0) {
            queryParams.append('filters', JSON.stringify(filters));
        }

        // If a batchId is provided, include it in the query string
        if (batchId) {
            queryParams.append('batchId', batchId);
        }
        queryParams.append('page', page);

        const response = await fetch(`${getBaseUrl()}/training/videos?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch videos');
        }

        const data = await response.json();
        
        if (!data.success) {
            throw new Error('Failed to fetch videos: Server returned unsuccessful response');
        }

        if (append) {
            setVideos(prev => [...prev, ...data.videos]);
        } else {
            setVideos(data.videos);
        }
        
        return {
            success: true,
            pagination: data.pagination
        };
    } catch (error) {
        setError('Failed to fetch videos. Please try again.');
        return {
            success: false,
            pagination: {
                offset: 0,
                limit: 20,
                totalCount: 0
            }
        };
    }
};

/**
 * Initializes the user's camera for video recording.
 * Sets up the video stream with specified dimensions and connects it to the video element.
 * 
 * @param {React.RefObject} videoRef - Reference to the video element to display the camera feed
 * @param {Function} setStream - React state setter to store the media stream
 * @param {Function} setError - React state setter to update the error message
 * @returns {Promise<void>}
 */
export const initCamera = async (videoRef, setStream, setError) => {
    try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ 
            video: { 
                width: { ideal: 1280 },
                height: { ideal: 720 },
                aspectRatio: 16/9,
                frameRate: { exact: 15 }
            } 
        });
        setStream(mediaStream);
        if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
        }
    } catch (err) {
        setError('Failed to access camera');
    }
};

/**
 * Creates a frame-limited MediaRecorder that automatically stops after a specified number of frames.
 * 
 * @param {MediaStream} stream - The media stream to record from
 * @param {number} targetFrameCount - Number of frames to record before stopping
 * @param {number} targetFps - Target frames per second for recording
 * @param {Function} onDataAvailable - Callback for when data is available
 * @param {Function} onStop - Callback for when recording stops
 * @returns {MediaRecorder} The configured MediaRecorder instance
 */
export const createFrameLimitedRecorder = (stream, targetFrameCount, targetFps, onDataAvailable, onStop) => {
    try {
        if (!stream || !stream.active) {
            return null;
        }

        const recorder = new MediaRecorder(stream, {
            mimeType: 'video/webm;codecs=vp8'
        });
        
        let frameCount = 0;
        let startTime = null;
        let animationFrameId = null;
        let isRecording = true;

        const processFrame = (timestamp) => {
            if (!isRecording) return;
            
            if (!startTime) startTime = timestamp;
            
            const elapsedTime = timestamp - startTime;
            const expectedFrames = Math.floor((elapsedTime / 1000) * targetFps);

            if (expectedFrames > frameCount && frameCount < targetFrameCount) {
                frameCount++;
                recorder.requestData();
                animationFrameId = requestAnimationFrame(processFrame);
            } else if (frameCount >= targetFrameCount) {
                isRecording = false;
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                }
                try {
                    recorder.stop();
                } catch (err) {
                    onStop();
                }
            } else {
                animationFrameId = requestAnimationFrame(processFrame);
            }
        };

        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
                onDataAvailable(event.data);
            }
        };

        recorder.onerror = (event) => {
            isRecording = false;
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            onStop();
        };

        recorder.onstop = () => {
            isRecording = false;
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            onStop();
        };

        try {
            recorder.start();
            animationFrameId = requestAnimationFrame(processFrame);
            return recorder;
        } catch (err) {
            return null;
        }
    } catch (err) {
        return null;
    }
};

/**
 * Manages the local storage cache for newly recorded videos
 */
const VIDEO_CACHE_KEY = 'newVideoMetadataCache';
const MAX_CACHED_BATCHES = 3;

export const cacheVideoMetadata = (videoMetadata) => {
    try {
        const existingCache = JSON.parse(localStorage.getItem(VIDEO_CACHE_KEY) || '{}');
        const batchCache = existingCache[videoMetadata.batchId] || [];
        batchCache.push(videoMetadata);
        existingCache[videoMetadata.batchId] = batchCache;

        // FIFO cache with 3 batch limit
        const batchIds = Object.keys(existingCache);
        if (batchIds.length > MAX_CACHED_BATCHES) {
            // Remove oldest batch(s) to maintain limit
            const batchesToRemove = batchIds.slice(0, batchIds.length - MAX_CACHED_BATCHES);
            batchesToRemove.forEach(batchId => {
                delete existingCache[batchId];
            });
        }

        localStorage.setItem(VIDEO_CACHE_KEY, JSON.stringify(existingCache));
    } catch (error) {
        console.error('Error caching video metadata:', error);
    }
};

export const getCachedVideosForBatch = (batchId) => {
    try {
        const cache = JSON.parse(localStorage.getItem(VIDEO_CACHE_KEY) || '{}');
        const cachedVideos = cache[batchId] || [];
        return cachedVideos;
    } catch (error) {
        return [];
    }
};

export const clearVideoMetadataCache = () => {
    localStorage.removeItem(VIDEO_CACHE_KEY);
};

export const removeFromVideoCache = (videoId, batchId) => {
    try {
        const cache = JSON.parse(localStorage.getItem(VIDEO_CACHE_KEY) || '{}');
        if (cache[batchId]) {
            cache[batchId] = cache[batchId].filter(video => video.id !== videoId);
            if (cache[batchId].length === 0) {
                delete cache[batchId];
            }
            localStorage.setItem(VIDEO_CACHE_KEY, JSON.stringify(cache));
        }
    } catch (error) {
    }
};

export const removeMultipleFromVideoCache = (videoIds) => {
    try {
        const cache = JSON.parse(localStorage.getItem(VIDEO_CACHE_KEY) || '{}');
        let modified = false;
        
        Object.keys(cache).forEach(batchId => {
            const originalLength = cache[batchId].length;
            cache[batchId] = cache[batchId].filter(video => !videoIds.includes(video.id));
            if (cache[batchId].length === 0) {
                delete cache[batchId];
            }
            if (cache[batchId]?.length !== originalLength) {
                modified = true;
            }
        });
        
        if (modified) {
            localStorage.setItem(VIDEO_CACHE_KEY, JSON.stringify(cache));
        }
    } catch (error) {
    }
};

/**
 * Uploads a recorded video blob to the training service.
 * Requires authentication and associates the video with a specific gesture.
 *
 * @param {Blob} blob - The recorded video data
 * @param {string} gesture - The sign language gesture associated with the video
 * @param {Function} setError - React state setter to update the error message
 * @param {Function} setVideos - React state setter to update the videos array
 * @param {string} [batch_id] - Optional batch ID to associate the video with a recording batch
 * @param {string} [gestureOrientation] - Optional orientation for the gesture
 * @param {string} [page='training'] - Page context for the upload
 * @param {Object} [metadata] - Optional metadata including rating, non_manuals, and notes
 * @returns {Promise<Object|null>} The uploaded video metadata or null if upload failed
 */
export const uploadVideo = async (blob, gesture, setError, setVideos, batch_id = null, gestureOrientation = null, page = 'training', metadata = null) => {
    try {
        const token = localStorage.getItem('jwt');
        if (!token) {
            setError('Not logged in');
            return null;
        }

        // Extract thumbnail from the video
        let thumbnailBlob = null;
        try {
            thumbnailBlob = await extractThumbnail(blob);
        } catch (err) {
        }

        const formData = new FormData();
        formData.append('video', blob);
        formData.append('gesture', gesture);
        if (gestureOrientation) {
            formData.append('gestureOrientation', gestureOrientation);
        }
        formData.append('page', page);
        if (metadata) {
            const metadataPayload = { ...metadata };
            try {
                formData.append('metadata', JSON.stringify(metadataPayload));
            } catch (err) {
                console.error('Failed to stringify metadata:', err);
            }
        }
        if (batch_id) {
            formData.append('batch_id', batch_id);
        }
        if (thumbnailBlob) {
            formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');
        }

        const response = await fetch(`${getBaseUrl()}/training/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
            const videoMetadata = {
                ...data.video,
                username: data.video.username || null,
                isCached: true
            };
            
            if (batch_id) {
                cacheVideoMetadata(videoMetadata);
            }
            
            return videoMetadata;
        } else {
            setError('Failed to upload video');
            return null;
        }
    } catch (err) {
        setError('Failed to upload video');
        return null;
    }
};

/**
 * Deletes a single training video by its ID.
 * Requires authentication and updates the UI after successful deletion.
 * 
 * @param {string} videoId - The ID of the video to delete
 * @param {Function} setError - React state setter to update the error message
 * @param {Function} onSuccess - Callback function to execute after successful deletion
 * @returns {Promise<void>}
 */
export const deleteSingle = async (videoId, setError, onSuccess) => {
    try {
        const token = localStorage.getItem('jwt');
        const encodedVideoId = encodeURIComponent(videoId);
        const response = await fetch(`${getBaseUrl()}/training/videos/${encodedVideoId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete video');
        }

        const data = await response.json();
        if (data.success) {
            // Extract batchId from videoId if it exists (assuming format includes batchId)
            const batchId = videoId.split('-')[0]; // Adjust this based on your videoId format
            removeFromVideoCache(videoId, batchId);
            onSuccess();
        }
    } catch (err) {
        setError(err.message || 'Failed to delete video');
    }
};

/**
 * Deletes multiple training videos in a batch operation.
 * Requires authentication and updates the UI after successful deletion.
 * 
 * @param {Set<string>} selectedVideos - Set of video IDs to delete
 * @param {Function} setSelectedVideos - React state setter to update the selection state
 * @param {Function} setError - React state setter to update the error message
 * @param {Function} onSuccess - Callback function to execute after successful deletion
 * @returns {Promise<void>}
 */
export const deleteSelected = async (selectedVideos, setSelectedVideos, setError, onSuccess) => {
    try {
        const token = localStorage.getItem('jwt');
        const videoIds = Array.from(selectedVideos);
        
        const response = await fetch(`${getBaseUrl()}/training/videos/batch`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ videoIds })
        });

        if (!response.ok) {
            throw new Error('Failed to delete videos');
        }

        const data = await response.json();
        if (data.success || data.partialSuccess) {
            removeMultipleFromVideoCache(videoIds);
            onSuccess();
            setSelectedVideos(new Set());
        }
    } catch (err) {
        setError('Failed to delete videos');
    }
};

/**
 * Updates the rating metadata for a specific video.
 * Requires authentication and propagates errors to the provided setError function.
 *
 * @param {string} videoId - ID of the video to update
 * @param {Object} rating - Rating data containing emotionScores, sentiment, speed, non_manuals, and notes
 * @param {Function} setError - Error state setter
 * @returns {Promise<Object>} The updated rating object
 */
export const updateVideoRating = async (videoId, rating, setError) => {
    try {
        const token = localStorage.getItem('jwt');
        if (!token) {
            throw new Error('Not logged in');
        }

        const response = await fetch(`${getBaseUrl()}/training/video/${encodeURIComponent(videoId)}/rating`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            // rating may include non_manuals and notes
            body: JSON.stringify(rating)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to update rating');
        }

        const data = await response.json();
        if (data.success) {
            return data.rating;
        }
        throw new Error('Failed to update rating');
    } catch (err) {
        if (setError) {
            setError(err.message || 'Failed to update rating');
        }
        throw err;
    }
};

/**
 * Validates recording settings and returns any errors
 *
 * @param {string} gesture - Selected gesture
 * @param {number} batchSize - Selected batch size
 * @param {number} countdownLength - Selected countdown length
 * @param {MediaStream} stream - Media stream
 * @param {Function} setError - Error state setter
 * @param {Function} setGestureError - Gesture error state setter
 * @param {Function} setBatchSizeError - Batch size error state setter
 * @param {Function} setCountdownLengthError - Countdown length error state setter
 * @returns {boolean} - Whether validation passed
 */
export const validateRecordingSettings = (
    gesture,
    stream,
    setError,
    setGestureError
) => {
    let hasError = false;
    if (!gesture) {
        setGestureError(true);
        hasError = true;
    }
    if (!stream) {
        setError("Camera not initialized");
        return true;
    }
    return hasError;
};

/**
 * Handles the recording process for a single video in a batch
 * 
 * @param {number} count - Current recording count
 * @param {number} batchSize - Total recordings in batch
 * @param {string} batchId - Batch identifier
 * @param {MediaStream} stream - Media stream
 * @param {Function} startCountdown - Countdown function
 * @param {Function} setIsRecording - Recording state setter
 * @param {Function} setCurrentCount - Current count state setter
 * @param {Function} setIsUploading - Upload state setter
 * @param {Function} setError - Error state setter
 * @param {Function} setVideos - React state setter to update the videos array
 * @param {string} gesture - Selected gesture
 * @param {boolean} isFirewallMode - Whether firewall mode is enabled
 * @param {Array} currentBatchRecordingsRef - Reference to current batch recordings
 * @param {boolean} viewAllUsers - Whether to view all users
 * @param {Function} updateGestureCounts - Function to update gesture counts
 * @param {Function} onBatchComplete - Callback function to execute when batch is complete
 */

export const handleRecordingProcess = async (
    count,
    batchSize,
    batchId,
    stream,
    startCountdown,
    setIsRecording,
    setCurrentCount,
    setIsUploading,
    setError,
    setVideos,
    gesture,
    isFirewallMode,
    currentBatchRecordingsRef,
    viewAllUsers = false,
    updateGestureCounts,
    orientation = null,
    skipInitialCountdown = false,
    incrementOverallCount = null,
    page = 'training',
    onBatchComplete
) => {
    const updateVideosState = (newVideos) => {
        setVideos(prev => {
            const updated = [
                ...newVideos.map(v => ({ ...v, username: v.username || null })),
                ...prev
            ];
            return updated;
        });
    };

    if (count >= batchSize) {
        if (onBatchComplete) onBatchComplete();
        return;
    }

    try {
        if (!(skipInitialCountdown && count === 0)) {
            await startCountdown();
        }

        const recordOnce = () => new Promise((resolve, reject) => {
            const chunks = [];
            const recorder = createFrameLimitedRecorder(
                stream,
                30,
                15,
                d => chunks.push(d),
                async () => {
                    setIsUploading(true);
                    try {
                        if (chunks.length === 0) throw new Error('No video data recorded');

                        const blob = new Blob(chunks, { type: 'video/webm' });

                        if (isFirewallMode) {
                            const rec = { blob, gesture, batch_id: batchId, recordingNumber: count + 1, orientation };
                            currentBatchRecordingsRef.current.push(rec);

                            if (count + 1 >= batchSize) {
                                try {
                                    const recordings = currentBatchRecordingsRef.current;
                                    const uploadedVideos = [];
                                    for (const r of recordings) {
                                        const meta = await uploadVideo(r.blob, r.gesture, setError, setVideos, r.batch_id, r.orientation, page);
                                        if (meta) uploadedVideos.push(meta);
                                    }
                                    if (uploadedVideos.length > 0) {
                                        updateVideosState(uploadedVideos);
                                        if (updateGestureCounts) updateGestureCounts(gesture, uploadedVideos.length);
                                    }
                                    currentBatchRecordingsRef.current = [];
                                } catch (err) {
                                    setError('Failed to upload batch. Please try again.');
                                    currentBatchRecordingsRef.current = [];
                                }
                            }
                        } else {
                            const meta = await uploadVideo(blob, gesture, setError, setVideos, batchId, orientation, page);
                            if (meta) {
                                updateVideosState([meta]);
                                if (updateGestureCounts) updateGestureCounts(gesture, 1);
                            }
                        }
                        resolve();
                    } catch (err) {
                        reject(err);
                    } finally {
                        setIsUploading(false);
                    }
                }
            );
            if (!recorder) reject(new Error('Failed to create recorder'));
        });

        await recordOnce();

        setCurrentCount(count + 1);
        if (incrementOverallCount) incrementOverallCount();

        if (count + 1 < batchSize) {
            await handleRecordingProcess(
                count + 1,
                batchSize,
                batchId,
                stream,
                startCountdown,
                setIsRecording,
                setCurrentCount,
                setIsUploading,
                setError,
                setVideos,
                gesture,
                isFirewallMode,
                currentBatchRecordingsRef,
                viewAllUsers,
                updateGestureCounts,
                orientation,
                false,
                incrementOverallCount,
                page,
                onBatchComplete
            );
        } else {
            if (onBatchComplete) onBatchComplete();
        }
    } catch (err) {
        setError('Failed to start recording. Please try again.');
        setIsRecording(false);
        setCurrentCount(0);
        currentBatchRecordingsRef.current = [];
    }
};
/**
 * Fetches the video URL for a given video ID.
 * Requires authentication via JWT token.
 * 
 * @param {string} videoId - The ID of the video to fetch
 * @returns {Promise<string>} The video URL
 */
export const fetchVideoUrl = async (videoId) => {
    const token = localStorage.getItem('jwt');
    if (!token) {
        throw new Error('Not logged in');
    }

    const response = await fetch(`${getBaseUrl()}/training/video/${encodeURIComponent(videoId)}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch video URL');
    }
    
    const data = await response.json();
    if (data.success) {
        return data.videoUrl;
    } else {
        throw new Error(data.error || 'Failed to fetch video URL');
    }
};

/**
 * Fetches a sample video URL for the given gesture recorded by user "adrian".
 * This does not require authentication.
 *
 * @param {string} gesture - Gesture to fetch sample for
 * @returns {Promise<string>} Sample video URL
 */
export const fetchSampleVideoUrl = async (gesture) => {
    const response = await fetch(`${getBaseUrl()}/training/sample?gesture=${encodeURIComponent(gesture)}`);
    if (!response.ok) {
        throw new Error('Failed to fetch sample video');
    }
    const data = await response.json();
    if (data.success) {
        return data.videoUrl;
    }
    throw new Error(data.error || 'Failed to fetch sample video');
};

/**
 * Fetches a sample video URL for a complete phrase recorded by user "adrian".
 * The backend stores these videos under the gesture name `${phraseId}_normal`.
 *
 * @param {string} phraseId - ID of the phrase to fetch the sample for
 * @returns {Promise<string>} Sample phrase video URL
 */
export const fetchSamplePhraseVideoUrl = async (phraseId) => {
    return fetchSampleVideoUrl(`${phraseId}_normal`);
};

/**
 * Fetches list of gestures that have a sample video recorded by user "adrian".
 * This does not require authentication.
 *
 * @returns {Promise<Array<string>>} Array of gesture names
 */
export const fetchSampleGestures = async () => {
    const response = await fetch(`${getBaseUrl()}/training/sample-gestures`);
    if (!response.ok) {
        throw new Error('Failed to fetch sample gestures');
    }
    const data = await response.json();
    if (data.success) {
        return data.gestures;
    }
    throw new Error(data.error || 'Failed to fetch sample gestures');
};

/**
 * Fetches gesture counts for the currently authenticated user.
 * If viewAllUsers is true and user is admin, fetches counts for all users.
 * 
 * @param {boolean} viewAllUsers - Whether to fetch counts from all users (requires admin role)
 * @returns {Promise<Object>} Object containing total count and counts per gesture
 */
export const fetchGestureCounts = async (viewAllUsers = false, userId = null, page = 'training') => {
    try {
        const token = localStorage.getItem('jwt');
        if (!token) {
            throw new Error('Not logged in');
        }

        const url = new URL(`${getBaseUrl()}/training/counts`);
        if (viewAllUsers) {
            url.searchParams.append('all_users', 'true');
        }
        if (userId) {
            url.searchParams.append('userId', userId);
        }
        url.searchParams.append('page', page);

        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
            return {
                total: data.total,
                counts: data.counts
            };
        } else {
            throw new Error('Failed to fetch gesture counts');
        }
    } catch (err) {
        throw err;
    }
};

/**
 * Fetch a list of users for admin filtering.
 * @returns {Promise<Array<{id: string, name: string}>>}
 */
export const fetchUsers = async () => {
    const token = localStorage.getItem('jwt');
    if (!token) {
        throw new Error('Not logged in');
    }
    const response = await fetch(`${getBaseUrl()}/training/users`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
        }
    });
    if (!response.ok) {
        throw new Error('Failed to fetch users');
    }
    const data = await response.json();
    if (data.success) {
        return data.users;
    }
    throw new Error(data.error || 'Failed to fetch users');
};

/**
 * Fetches training batches.
 * Applies pagination and the viewAllUsers flag.
 *
 * @param {Function} setBatches - React state setter for the batches list
 * @param {Function} setError - React state setter for error messages
 * @param {boolean} viewAllUsers - Whether to fetch batches for all users (admin mode)
 * @param {number} offset - Pagination offset
 * @param {number} limit - Pagination limit
 * @param {Array} filters - Array of gesture filters to apply
 * @returns {Promise<Object>} - Object with success flag and pagination info
 */
export const fetchBatches = async (
    setBatches,
    setError,
    viewAllUsers = false,
    offset = 0,
    limit = 20,
    filters = [],
    userId = null,
    minStart = null,
    page = 'training'
) => {
    try {
        const token = localStorage.getItem('jwt');
        if (!token) {
            throw new Error('No authentication token found');
        }
        const queryParams = new URLSearchParams({
            offset: offset.toString(),
            limit: limit.toString(),
            all_users: viewAllUsers.toString()
        });

        // Add filters to query params if they exist
        if (userId) {
            queryParams.append("userId", userId);
        }
        if (filters.length > 0) {
            queryParams.append('filters', JSON.stringify(filters));
        }
        if (minStart) {
            queryParams.append('minStart', new Date(minStart).toISOString());
        }
        queryParams.append('page', page);

        const response = await fetch(`${getBaseUrl()}/training/batches?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        if (!response.ok) {
            throw new Error('Failed to fetch batches');
        }
        const data = await response.json();
        if (!data.success) {
            throw new Error('Server returned unsuccessful response for batches');
        }
        setBatches(prev => offset === 0 ? data.batches : [...prev, ...data.batches]);
        return {
            success: true,
            pagination: data.pagination,
            batches: data.batches
        };
    } catch (error) {
        setError('Failed to fetch batches. Please try again.');
        return {
            success: false,
            pagination: { offset: 0, limit: 20, totalCount: 0 }
        };
    }
};

/**
 * Filters gestures based on a search query
 * @param {Object} category - The category containing gestures to filter
 * @param {string} searchQuery - The search query to filter by
 * @returns {Array} Filtered list of gestures
 */
export const getFilteredGestures = (category, searchQuery, page = 'training', phraseMap = {}) => {
    if (!searchQuery) return category.gestures;
    const query = searchQuery.toLowerCase();
    return category.gestures.filter(gesture => {
        const label = page === 'phrase' ? (phraseMap[gesture] || gesture) : gesture;
        return label.toLowerCase().includes(query);
    });
};

/**
 * Checks if a category has any gestures matching the search query
 * @param {Object} category - The category to check
 * @param {string} searchQuery - The search query to match against
 * @returns {boolean} Whether the category has any matching gestures
 */
export const categoryHasMatches = (category, searchQuery, page = 'training', phraseMap = {}) => {
    return getFilteredGestures(category, searchQuery, page, phraseMap).length > 0;
};
// Workflow utility functions
export const ORIENTATION_INSTRUCTIONS = {
    front: "Face the camera (normal speed & distance)",
    left: "Turn your body to your left (will appear right on screen)",
    right: "Turn your body to your right (will appear left on screen)",
    slow: "Perform a bit slower",
    fast: "Perform a bit faster",
    close: "Move a bit closer to the camera",
    far: "Move a bit further from the camera"
};

export const WORKFLOW_STEPS_22 = [
    { instruction: ORIENTATION_INSTRUCTIONS.front, count: 5, orientation: "front" },
    { instruction: ORIENTATION_INSTRUCTIONS.left, count: 3, orientation: "left" },
    { instruction: ORIENTATION_INSTRUCTIONS.right, count: 3, orientation: "right" },
    { instruction: ORIENTATION_INSTRUCTIONS.slow, count: 3, orientation: "slow" },
    { instruction: ORIENTATION_INSTRUCTIONS.fast, count: 3, orientation: "fast" },
    { instruction: ORIENTATION_INSTRUCTIONS.close, count: 3, orientation: "close" },
    { instruction: ORIENTATION_INSTRUCTIONS.far, count: 2, orientation: "far" }
];

export const WORKFLOW_STEPS_20 = [
    { instruction: ORIENTATION_INSTRUCTIONS.front, count: 5, orientation: "front" },
    { instruction: ORIENTATION_INSTRUCTIONS.left, count: 3, orientation: "left" },
    { instruction: ORIENTATION_INSTRUCTIONS.right, count: 3, orientation: "right" },
    { instruction: ORIENTATION_INSTRUCTIONS.slow, count: 2, orientation: "slow" },
    { instruction: ORIENTATION_INSTRUCTIONS.fast, count: 3, orientation: "fast" },
    { instruction: ORIENTATION_INSTRUCTIONS.close, count: 2, orientation: "close" },
    { instruction: ORIENTATION_INSTRUCTIONS.far, count: 2, orientation: "far" }
];

export const ADRIAN_WORKFLOW_STEPS = [
    { instruction: ORIENTATION_INSTRUCTIONS.front, count: 2, orientation: "front" },
    { instruction: ORIENTATION_INSTRUCTIONS.left, count: 2, orientation: "left" },
    { instruction: ORIENTATION_INSTRUCTIONS.right, count: 2, orientation: "right" },
    { instruction: ORIENTATION_INSTRUCTIONS.slow, count: 1, orientation: "slow" },
    { instruction: ORIENTATION_INSTRUCTIONS.fast, count: 1, orientation: "fast" },
    { instruction: ORIENTATION_INSTRUCTIONS.close, count: 1, orientation: "close" },
    { instruction: ORIENTATION_INSTRUCTIONS.far, count: 1, orientation: "far" }
];

export const getWorkflowStepsForCategory = (categoryId, isAdrian = false) => {
    if (isAdrian) return ADRIAN_WORKFLOW_STEPS;
    if (["unit1","unit2","unit3","letters","numbers","numberingSystems"].includes(categoryId)) {
        return WORKFLOW_STEPS_20;
    }
    return WORKFLOW_STEPS_22;
};

export const getOrientationCountsForCategory = (categoryId, isAdrian = false) => {
    const steps = getWorkflowStepsForCategory(categoryId, isAdrian);
    return steps.reduce((acc, step) => {
        acc[step.orientation] = (acc[step.orientation] || 0) + step.count;
        return acc;
    }, {});
};

export const getExpectedTotalForCategory = (categoryId, isAdrian = false) => {
    const counts = getOrientationCountsForCategory(categoryId, isAdrian);
    return Object.values(counts).reduce((a,b) => a + b, 0);
};

// Retrieve user settings from the training service
export const fetchUserSettings = async () => {
    const token = localStorage.getItem('jwt');
    if (!token) throw new Error('Not logged in');

    const response = await fetch(`${getBaseUrl()}/training/user-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
        throw new Error('Failed to fetch user settings');
    }

    const data = await response.json();
    if (data.success) {
        return data.settings || {};
    }
    throw new Error(data.error || 'Failed to fetch user settings');
};

// Save user settings to the training service
export const saveUserSettings = async (settings) => {
    const token = localStorage.getItem('jwt');
    if (!token) throw new Error('Not logged in');

    const response = await fetch(`${getBaseUrl()}/training/user-settings`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
    });

    if (!response.ok) {
        throw new Error('Failed to save user settings');
    }

    const data = await response.json();
    if (data.success) {
        return data.settings;
    }
    throw new Error(data.error || 'Failed to save user settings');
};
